import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import * as actions from 'global/actions/signUpActions';
import { useActions } from 'hooks/useActions';
import { useAppContext } from 'hooks/useContext';
import * as strings from './strings';
import Path from 'routes/Path';
import apiErrors from 'api/apiErrors';
import { getCookie } from 'functions/getCookie';
import { Container, Wrapper } from 'components/Layout/Layout';
import { FilterCheckbox } from 'components/Checkbox/Checkbox';
import { ButtonsContainer, RectangleButton } from 'components/Button/RectangleButton';
import { Form } from 'components/FormComponents/FormComponents';
import SignInWithGoogle from 'components/ExternalSignIn/SignInWithGoogle';
import SignInWithLinkedIn from 'components/ExternalSignIn/SignInWithLinkedIn';
import Divider from 'components/Divider/Divider';
import {
  AuthPageBackgroundImage,
  AuthContainer,
  Heading,
  InputContainer,
  InputField,
  Icon,
  InfoText,
  LinkToPage,
  AuthPageGradient,
  TextContainer,
  Label,
} from '../LoginPage/styles';

export default function SignupPage() {
  const navigate = useNavigate();
  const { language } = useAppContext();

  const { loading, error, success } = useSelector((state) => state.signUp);
  const { clearError, clearState, signup } = useActions(actions);

  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [name, setName] = useState('');
  const [isCompany, setIsCompany] = useState(false);
  const [isAgreementAgreed, setIsAgreementAgreed] = useState(false);
  const [isGDPRAgreed, setIsGDPRAgreed] = useState(false);
  const [campaignSource, setCampaignSource] = useState('');

  useEffect(() => {
    if (getCookie('campaignSource')) setCampaignSource(getCookie('campaignSource'));
  }, []);

  useEffect(() => {
    return () => clearState();
  }, [clearState]);

  return (
    <Wrapper isNewPage>
      <AuthPageBackgroundImage>
        <AuthPageGradient />
      </AuthPageBackgroundImage>
      <Container column useZIndex="1">
        <AuthContainer isSignUpPage>
          <Heading>{strings.headerLabel[language]}</Heading>
          <Form
            onChange={() => error && clearError()}
            onSubmit={() => {
              signup(
                email,
                company,
                name,
                isCompany,
                isAgreementAgreed,
                isGDPRAgreed,
                campaignSource ? campaignSource : undefined
              );
            }}
          >
            <InputContainer hasError={error}>
              <Icon className="fa-regular fa-envelope" />
              <InputField
                type="email"
                placeholder={strings.emailPlaceholder[language]}
                hasError={error}
                value={email}
                disabled={success}
                onChange={(e) => {
                  clearState();
                  setEmail(e.target.value);
                }}
              />
            </InputContainer>
            {isCompany && (
              <>
                <InputContainer>
                  <Icon className="fa-regular fa-user" />
                  <InputField
                    type="text"
                    placeholder={strings.namePlaceholder[language]}
                    value={name}
                    disabled={success}
                    onChange={(e) => setName(e.target.value)}
                  />
                </InputContainer>
                <InputContainer>
                  <Icon className="fa-regular fa-building" />
                  <InputField
                    type="text"
                    placeholder={strings.companyPlaceholder[language]}
                    value={company}
                    disabled={success}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </InputContainer>
              </>
            )}
            <Label>
              <FilterCheckbox
                isSignUpPage
                onChange={() => setIsCompany(!isCompany)}
                checked={isCompany}
                label={strings.companyLabel[language]}
              />
            </Label>
            {!isCompany && (
              <>
                <Label>
                  <FilterCheckbox
                    isSignUpPage
                    onChange={() => setIsAgreementAgreed(!isAgreementAgreed)}
                    checked={isAgreementAgreed}
                    label={strings.agreementLabel[language]}
                  />
                </Label>
                <Label>
                  <FilterCheckbox
                    isSignUpPage
                    onChange={() => setIsGDPRAgreed(!isGDPRAgreed)}
                    checked={isGDPRAgreed}
                    label={strings.GDPRLabel[language]}
                  />
                </Label>
              </>
            )}
            <ButtonsContainer padding="20px 0 0">
              <RectangleButton
                type="submit"
                isAuthButton
                padding="20px 0 0"
                disabled={
                  !email ||
                  (isCompany && !name) ||
                  (isCompany && !company) ||
                  (!isCompany && !isAgreementAgreed) ||
                  (!isCompany && !isGDPRAgreed) ||
                  error ||
                  success
                }
              >
                {strings.signupLabel[language]}
              </RectangleButton>
            </ButtonsContainer>
          </Form>
          {(error || success) && (
            <InfoText error={error} success={success}>
              {loading ? strings.loadingLabel[language] : ''}
              {apiErrors?.[error?.name]?.[language] ?? error?.[language] ?? error}
              {success ? strings.successLabel[language] : ''}
            </InfoText>
          )}
          {!isCompany && (
            <>
              <Divider text="or" />
              <SignInWithGoogle />
              <SignInWithLinkedIn />
            </>
          )}
          <LinkToPage onClick={() => navigate(Path.loginPage)}>{strings.loginLabel[language]}</LinkToPage>
          <TextContainer>
            Sign up to become a member of the coding community. To sign up for coding tests you need to create a company
            account.
          </TextContainer>
        </AuthContainer>
      </Container>
    </Wrapper>
  );
}
